import React from "react";

/**
 * Professional certificate frame with asymmetric navy curves and gold accents
 * (matches the reference "Certificate of Achievement" template).
 *
 * Two variants:
 *  - variant="gold"  → deep navy + strong gold  (Experto Canales)
 *  - variant="blue"  → deep navy + softer amber (regular exam)
 *
 * Renders as an SVG that scales to its container. Pass the certificate content
 * as children — it will be positioned above the frame decoration.
 */
export const CertificateFrame = ({ variant = "gold", children, className = "" }) => {
  const NAVY = "#0d1a3f";
  const NAVY_LT = "#233464";
  const GOLD = variant === "gold" ? "#f4a300" : "#f7c04b";
  const GOLD_LT = variant === "gold" ? "#ffd166" : "#ffdd8a";
  const CREAM = "#fbf3d9";
  const DOT = "rgba(255,255,255,0.28)";

  const W = 1200, H = 848;

  // Halftone dot pattern id
  const dotId = `dot-${variant}`;
  const texId = `tex-${variant}`;

  // Left cascading curves (mirrored on right)
  const leftPaths = [
    // Outer navy sweep
    { fill: NAVY, d: `M0,0 L340,0 C340,120 100,180 200,340 C300,500 40,560 130,730 C170,830 60,${H} 0,${H} Z` },
    // Halftone-textured navy panel
    { fill: `url(#${dotId})`, d: `M0,60 L300,20 C300,140 90,190 190,340 C290,490 60,570 130,720 C170,820 40,${H - 20} 0,${H - 20} Z` },
    // Gold curve
    { fill: GOLD, d: `M0,80 L230,10 C240,140 60,190 160,350 C260,510 30,600 110,760 C140,840 20,${H} 0,${H} Z` },
    // Deeper gold accent
    { fill: GOLD_LT, d: `M0,180 L170,90 C180,220 30,270 130,410 C230,550 0,650 90,800 L0,${H} Z` },
    // Inner navy again
    { fill: NAVY, d: `M0,280 L110,220 C120,340 20,380 100,500 C180,620 10,720 60,830 L0,${H} Z` },
  ];

  return (
    <div className={`relative ${className}`} style={{ aspectRatio: `${W}/${H}` }}>
      <svg viewBox={`0 0 ${W} ${H}`} className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
        <defs>
          <pattern id={dotId} x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
            <rect width="14" height="14" fill={NAVY_LT} />
            <circle cx="4" cy="4" r="1.6" fill={DOT} />
          </pattern>
          <pattern id={texId} x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
            <rect width="24" height="24" fill={CREAM} />
            <circle cx="4" cy="4" r="0.9" fill="rgba(180,120,20,0.15)" />
            <circle cx="14" cy="14" r="0.9" fill="rgba(180,120,20,0.15)" />
          </pattern>
        </defs>

        {/* Cream textured background */}
        <rect width={W} height={H} fill={`url(#${texId})`} />

        {/* Left decorative shapes */}
        <g>
          {leftPaths.map((p, i) => (
            <path key={i} d={p.d} fill={p.fill} />
          ))}
        </g>

        {/* Right decorative shapes (mirror) */}
        <g transform={`translate(${W},0) scale(-1,1)`}>
          {leftPaths.map((p, i) => (
            <path key={i} d={p.d} fill={p.fill} />
          ))}
        </g>

        {/* Outer thin gold frame */}
        <rect
          x="24" y="24"
          width={W - 48} height={H - 48}
          fill="none"
          stroke={GOLD}
          strokeWidth="4"
        />
      </svg>

      {/* Content overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-[62%] text-center px-4">{children}</div>
      </div>
    </div>
  );
};

/**
 * Ornamental gold divider (leaf + curls) used under the title.
 * Sits between the title and the "presented to" line.
 */
export const CertificateDivider = ({ color = "#f4a300" }) => (
  <svg viewBox="0 0 240 24" className="mx-auto my-3" style={{ width: 220, height: 22 }}>
    <line x1="0" y1="12" x2="90" y2="12" stroke={color} strokeWidth="1.8" />
    <line x1="150" y1="12" x2="240" y2="12" stroke={color} strokeWidth="1.8" />
    <path
      d="M90 12 C 100 4, 110 4, 120 12 C 130 20, 140 20, 150 12"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path d="M115 12 l5 -6 l5 6 l-5 6 z" fill={color} />
    <circle cx="90" cy="12" r="2.4" fill={color} />
    <circle cx="150" cy="12" r="2.4" fill={color} />
  </svg>
);

/**
 * Draw the professional certificate frame directly onto a jsPDF document
 * (landscape orientation). Mirrors the SVG variant above so the printed
 * output matches the preview.
 */
export const drawCertificateFrame = (doc, W, H, variant = "gold") => {
  const NAVY = [13, 26, 63];
  const NAVY_LT = [35, 52, 100];
  const GOLD = variant === "gold" ? [244, 163, 0] : [247, 192, 75];
  const GOLD_LT = variant === "gold" ? [255, 209, 102] : [255, 221, 138];
  const CREAM = [251, 243, 217];

  // Cream base
  doc.setFillColor(...CREAM);
  doc.rect(0, 0, W, H, "F");

  // Subtle texture dots on cream
  doc.setFillColor(180, 120, 20);
  doc.setGState && doc.setGState(new doc.GState({ opacity: 0.12 }));
  for (let x = 20; x < W - 20; x += 22) {
    for (let y = 20; y < H - 20; y += 22) {
      doc.circle(x, y, 0.6, "F");
      doc.circle(x + 11, y + 11, 0.6, "F");
    }
  }
  doc.setGState && doc.setGState(new doc.GState({ opacity: 1 }));

  // Helper: draw one side's stack of curves. `mirror` flips horizontally
  const scaleX = W / 1200;
  const scaleY = H / 848;
  const S = (x, y) => [x * scaleX, y * scaleY];

  const drawSide = (mirror) => {
    const flip = (x) => (mirror ? W - x : x);
    // Each layer: sequence of [op, points...] using jsPDF's line + curve API
    const layers = [
      // Outer navy sweep
      { fill: NAVY, poly: (m) => {
        const [x0, y0] = S(0, 0);
        const [x1, y1] = S(340, 0);
        const [x2, y2] = S(200, 340);
        const [c1x, c1y] = S(340, 120);
        const [c2x, c2y] = S(100, 180);
        const [x3, y3] = S(130, 730);
        const [c3x, c3y] = S(300, 500);
        const [c4x, c4y] = S(40, 560);
        const [x4, y4] = S(0, 848);
        const [c5x, c5y] = S(170, 830);
        const [c6x, c6y] = S(60, 848);
        return { start: [flip(x0), y0], curves: [
          ["L", flip(x1), y1],
          ["C", flip(c1x), c1y, flip(c2x), c2y, flip(x2), y2],
          ["C", flip(c3x), c3y, flip(c4x), c4y, flip(x3), y3],
          ["C", flip(c5x), c5y, flip(c6x), c6y, flip(x4), y4],
          ["L", flip(x0), y4],
        ]};
      }},
      // Halftone-look navy panel (using darker navy to hint texture)
      { fill: NAVY_LT, poly: (m) => {
        const [x0, y0] = S(0, 60);
        const [x1, y1] = S(300, 20);
        const [x2, y2] = S(190, 340);
        const [c1x, c1y] = S(300, 140);
        const [c2x, c2y] = S(90, 190);
        const [x3, y3] = S(130, 720);
        const [c3x, c3y] = S(290, 490);
        const [c4x, c4y] = S(60, 570);
        const [xEnd, yEnd] = S(0, 828);
        const [c5x, c5y] = S(170, 820);
        const [c6x, c6y] = S(40, 828);
        return { start: [flip(x0), y0], curves: [
          ["L", flip(x1), y1],
          ["C", flip(c1x), c1y, flip(c2x), c2y, flip(x2), y2],
          ["C", flip(c3x), c3y, flip(c4x), c4y, flip(x3), y3],
          ["C", flip(c5x), c5y, flip(c6x), c6y, flip(xEnd), yEnd],
          ["L", flip(x0), yEnd],
        ]};
      }},
      // Gold curve
      { fill: GOLD, poly: (m) => {
        const [x0, y0] = S(0, 80);
        const [x1, y1] = S(230, 10);
        const [x2, y2] = S(160, 350);
        const [c1x, c1y] = S(240, 140);
        const [c2x, c2y] = S(60, 190);
        const [x3, y3] = S(110, 760);
        const [c3x, c3y] = S(260, 510);
        const [c4x, c4y] = S(30, 600);
        const [xEnd, yEnd] = S(0, 848);
        const [c5x, c5y] = S(140, 840);
        const [c6x, c6y] = S(20, 848);
        return { start: [flip(x0), y0], curves: [
          ["L", flip(x1), y1],
          ["C", flip(c1x), c1y, flip(c2x), c2y, flip(x2), y2],
          ["C", flip(c3x), c3y, flip(c4x), c4y, flip(x3), y3],
          ["C", flip(c5x), c5y, flip(c6x), c6y, flip(xEnd), yEnd],
          ["L", flip(x0), yEnd],
        ]};
      }},
      // Gold-lite highlight
      { fill: GOLD_LT, poly: (m) => {
        const [x0, y0] = S(0, 180);
        const [x1, y1] = S(170, 90);
        const [x2, y2] = S(130, 410);
        const [c1x, c1y] = S(180, 220);
        const [c2x, c2y] = S(30, 270);
        const [xEnd, yEnd] = S(0, 848);
        const [c3x, c3y] = S(230, 550);
        const [c4x, c4y] = S(0, 650);
        const [x3, y3] = S(90, 800);
        return { start: [flip(x0), y0], curves: [
          ["L", flip(x1), y1],
          ["C", flip(c1x), c1y, flip(c2x), c2y, flip(x2), y2],
          ["C", flip(c3x), c3y, flip(c4x), c4y, flip(x3), y3],
          ["L", flip(x0), yEnd],
        ]};
      }},
      // Inner navy tongue
      { fill: NAVY, poly: (m) => {
        const [x0, y0] = S(0, 280);
        const [x1, y1] = S(110, 220);
        const [x2, y2] = S(100, 500);
        const [c1x, c1y] = S(120, 340);
        const [c2x, c2y] = S(20, 380);
        const [x3, y3] = S(60, 830);
        const [c3x, c3y] = S(180, 620);
        const [c4x, c4y] = S(10, 720);
        const [xEnd, yEnd] = S(0, 848);
        return { start: [flip(x0), y0], curves: [
          ["L", flip(x1), y1],
          ["C", flip(c1x), c1y, flip(c2x), c2y, flip(x2), y2],
          ["C", flip(c3x), c3y, flip(c4x), c4y, flip(x3), y3],
          ["L", flip(x0), yEnd],
        ]};
      }},
    ];

    // Draw each layer using paths (moveTo + curveTo/lineTo → fill)
    layers.forEach((layer) => {
      const shape = layer.poly(mirror);
      const [sx, sy] = shape.start;
      doc.setFillColor(...layer.fill);
      // jsPDF: use internal path via lines? better use path via context2d-free API:
      // We construct with doc.__private__ ? Simpler: use doc.setDrawColor and doc.moveTo/lineTo via ctx? jsPDF has doc.lines((relativeSegments), x, y).
      // We'll approximate with a polygon of many sampled points along the beziers.
      const pts = [[sx, sy]];
      for (const seg of shape.curves) {
        if (seg[0] === "L") {
          pts.push([seg[1], seg[2]]);
        } else if (seg[0] === "C") {
          const [_, c1x, c1y, c2x, c2y, ex, ey] = seg;
          const last = pts[pts.length - 1];
          const steps = 24;
          for (let i = 1; i <= steps; i++) {
            const t = i / steps;
            const mt = 1 - t;
            const bx = mt*mt*mt*last[0] + 3*mt*mt*t*c1x + 3*mt*t*t*c2x + t*t*t*ex;
            const by = mt*mt*mt*last[1] + 3*mt*mt*t*c1y + 3*mt*t*t*c2y + t*t*t*ey;
            pts.push([bx, by]);
          }
        }
      }
      // Convert absolute points → relative segments for doc.lines
      const rel = [];
      for (let i = 1; i < pts.length; i++) {
        rel.push([pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]]);
      }
      doc.lines(rel, sx, sy, [1, 1], "F", true);
    });
  };

  drawSide(false);
  drawSide(true);

  // Thin gold outer frame
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(2.4);
  doc.rect(20, 20, W - 40, H - 40, "S");
};

/**
 * Draw the ornamental gold divider on the PDF (leaf + side lines).
 */
export const drawCertificateDivider = (doc, cx, y, color = [244, 163, 0]) => {
  doc.setDrawColor(...color);
  doc.setLineWidth(1.4);
  doc.line(cx - 100, y, cx - 25, y);
  doc.line(cx + 25, y, cx + 100, y);
  // small arcs approximated with quadratic curve using lines
  const arc = (fromX, toX) => {
    const steps = 16;
    const w = toX - fromX;
    const p0 = [fromX, y];
    const rel = [];
    let prev = p0;
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const bx = fromX + w * t;
      const by = y - Math.sin(t * Math.PI) * 6;
      rel.push([bx - prev[0], by - prev[1]]);
      prev = [bx, by];
    }
    doc.lines(rel, p0[0], p0[1], [1, 1], "S", false);
  };
  arc(cx - 25, cx);
  arc(cx, cx + 25);
  // Leaf
  doc.setFillColor(...color);
  doc.triangle(cx - 5, y, cx, y - 6, cx + 5, y, "F");
  doc.triangle(cx - 5, y, cx, y + 6, cx + 5, y, "F");
  doc.circle(cx - 100, y, 2.4, "F");
  doc.circle(cx + 100, y, 2.4, "F");
};

/**
 * Draw the gold seal (scalloped circle with navy center) at (cx, cy).
 * Text lines are centered inside.
 */
export const drawSeal = (doc, cx, cy, textTop, textBottom, colors) => {
  const { GOLD, NAVY } = colors || { GOLD: [244, 163, 0], NAVY: [13, 26, 63] };
  // Scallop bumps
  for (let i = 0; i < 20; i++) {
    const a = (i / 20) * Math.PI * 2;
    doc.setFillColor(...GOLD);
    doc.circle(cx + Math.cos(a) * 42, cy + Math.sin(a) * 42, 6.5, "F");
  }
  doc.setFillColor(...GOLD);
  doc.circle(cx, cy, 42, "F");
  doc.setFillColor(...NAVY);
  doc.circle(cx, cy, 34, "F");
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(1.2);
  doc.circle(cx, cy, 32, "S");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...GOLD);
  doc.text(textTop, cx, cy - 4, { align: "center", charSpace: 1 });
  doc.setFontSize(8);
  doc.text(textBottom, cx, cy + 8, { align: "center", charSpace: 1 });
  doc.setFontSize(7);
  doc.text("★ ★ ★ ★ ★", cx, cy + 18, { align: "center" });
};

export default CertificateFrame;
